import React from 'react';
import { Phone, MapPin, Award, Users, Clock, Shield, CheckCircle, Wrench, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const whatsappNumber = '+966555150324';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace('+', '')}`;

  const stats = [
    { number: '10+', label: 'سنوات خبرة', icon: Award },
    { number: '15+', label: 'فني معتمد', icon: Users },
    { number: '24/7', label: 'خدمة مستمرة', icon: Clock },
    { number: '2000+', label: 'عميل راضٍ', icon: Shield }
  ];

  const values = [
    {
      icon: Shield,
      title: 'الجودة والاحترافية',
      description: 'نلتزم بأعلى معايير الجودة في تقديم خدماتنا مع ضمان شامل على جميع الأعمال'
    },
    {
      icon: Clock,
      title: 'السرعة في الاستجابة',
      description: 'فريقنا جاهز للوصول إليك في أي وقت على مدار الساعة طوال أيام الأسبوع'
    },
    {
      icon: Users,
      title: 'فريق محترف ومدرب',
      description: 'جميع فنيينا حاصلون على تدريب متخصص وشهادات معتمدة في مجالاتهم'
    },
    {
      icon: Wrench,
      title: 'أحدث المعدات',
      description: 'نستخدم أحدث الأدوات والمعدات لضمان إنجاز العمل بأعلى كفاءة'
    }
  ];

  const timeline = [
    {
      year: '2014',
      title: 'البداية',
      description: 'تأسيس هاوس كرافت في بريدة بفريق صغير من الفنيين المحترفين'
    },
    {
      year: '2017',
      title: 'التوسع',
      description: 'توسيع نطاق الخدمات وزيادة عدد الفنيين لتغطية جميع أحياء بريدة'
    },
    {
      year: '2020',
      title: 'الريادة',
      description: 'أصبحنا الخيار الأول للصيانة المنزلية في بريدة مع أكثر من 1000 عميل'
    },
    {
      year: '2024',
      title: 'التميز',
      description: 'تقديم خدمات متكاملة على مدار الساعة مع أحدث التقنيات'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" dir="rtl">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <a href="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_trusted-handyman-1/artifacts/bwyuu9dw_IMG_0972.jpeg" 
                  alt="هاوس كرافت" 
                  className="h-12 w-auto"
                />
              </a>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="/" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">الرئيسية</a>
              <a href="/services" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">الخدمات</a>
              <a href="/about" className="text-cyan-600 font-semibold">من نحن</a>
              <a href="/contact" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">اتصل بنا</a>
            </nav>
            <Button 
              onClick={() => window.open(whatsappLink, '_blank')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4 ml-2" />
              احجز الآن
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-white to-slate-50 opacity-60"></div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Award className="w-4 h-4" />
              10+ سنوات من التميز في بريدة
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              من نحن؟
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              هاوس كرافت هي شركة رائدة في مجال الصيانة المنزلية في بريدة، متخصصة في خدمات التكييف والكهرباء والسباكة. نفخر بتقديم حلول صيانة شاملة بأعلى معايير الجودة والاحترافية منذ عام 2014.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-cyan-600 to-cyan-700">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 text-center p-6 hover:bg-white/20 transition-all">
                <CardContent className="p-0">
                  <stat.icon className="w-12 h-12 text-white mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-cyan-50 font-semibold">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">قصتنا</h2>
            <p className="text-xl text-slate-600">
              بدأت رحلتنا من رؤية بسيطة: تقديم خدمات صيانة منزلية موثوقة وسريعة لأهالي بريدة
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center mb-16">
            <div>
              <img 
                src="https://housecraft.me/gfhdjsdhfd333.png" 
                alt="فريق العمل"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-slate-900">رؤيتنا ورسالتنا</h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                نسعى لأن نكون الخيار الأول والأكثر ثقة في مجال الصيانة المنزلية في بريدة والقصيم، من خلال تقديم خدمات عالية الجودة تفوق توقعات عملائنا.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                رسالتنا هي توفير حلول صيانة شاملة وسريعة بأيدي فنيين محترفين، مع ضمان رضا العملاء في كل مرة.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">مسيرتنا عبر السنين</h2>
            <p className="text-xl text-slate-600">رحلة نمو وتطور مستمر</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {item.year}
                    </div>
                  </div>
                  <Card className="flex-1 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">قيمنا ومبادئنا</h2>
            <p className="text-xl text-slate-600">ما يميزنا عن الآخرين</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-2xl transition-all border-2 hover:border-cyan-500 group">
                <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 to-cyan-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">فريقنا المحترف</h2>
            <p className="text-xl text-slate-600">فنيون معتمدون ومدربون على أعلى مستوى</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Card className="p-8">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold text-cyan-600 mb-2">15+</div>
                    <div className="text-slate-600">فني معتمد</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-cyan-600 mb-2">100%</div>
                    <div className="text-slate-600">مدربون ومعتمدون</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-cyan-600 mb-2">24/7</div>
                    <div className="text-slate-600">جاهزون للخدمة</div>
                  </div>
                </div>
                
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-cyan-600" />
                      <span>شهادات معتمدة في التكييف والتبريد</span>
                    </li>
                    <li className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-cyan-600" />
                      <span>خبراء في الأنظمة الكهربائية</span>
                    </li>
                    <li className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-cyan-600" />
                      <span>متخصصون في السباكة والصرف الصحي</span>
                    </li>
                    <li className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-cyan-600" />
                      <span>تدريب مستمر على أحدث التقنيات</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-cyan-600 to-cyan-700">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            انضم لعائلة عملائنا الراضين
          </h2>
          <p className="text-xl text-cyan-50 mb-8 max-w-2xl mx-auto">
            اتصل بنا الآن واحصل على خدمة صيانة احترافية في منزلك
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => window.open(whatsappLink, '_blank')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xl font-bold py-7 px-10 rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              <MessageCircle className="w-6 h-6 ml-2" />
              تواصل عبر واتساب
            </Button>
            <Button 
              size="lg"
              onClick={() => window.location.href = '/contact'}
              variant="outline"
              className="bg-white text-cyan-600 border-2 border-white hover:bg-cyan-50 text-xl font-bold py-7 px-10 rounded-xl transition-all"
            >
              <MapPin className="w-6 h-6 ml-2" />
              زر مقرنا
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-handyman-1/artifacts/bwyuu9dw_IMG_0972.jpeg" 
                alt="هاوس كرافت" 
                className="h-12 w-auto mb-4 brightness-200"
              />
              <p className="text-slate-400">
                شريكك الموثوق في الصيانة المنزلية ببريدة
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">روابط سريعة</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/" className="hover:text-white transition-colors">الرئيسية</a></li>
                <li><a href="/services" className="hover:text-white transition-colors">الخدمات</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">من نحن</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">اتصل بنا</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">خدماتنا</h3>
              <ul className="space-y-2 text-slate-400">
                <li>صيانة التكييف</li>
                <li>خدمات الكهرباء</li>
                <li>خدمات السباكة</li>
                <li>صيانة دورية</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">تواصل معنا</h3>
              <div className="space-y-3">
                <a href={`tel:${whatsappNumber}`} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  {whatsappNumber}
                </a>
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin className="w-5 h-5" />
                  <span>بريدة، القصيم</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>© 2024 هاوس كرافت. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 z-50 animate-pulse"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

export default About;
